import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Brain, 
  Users, 
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Target,
  Zap,
  Heart,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  BarChart3,
  Mail
} from 'lucide-react'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { Navigation } from '../../components/Navigation'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { useAuth } from '../../hooks/useAuth'
import { db } from '../../lib/supabase'
import { sendEmail, generateAssessmentReportEmail } from '../../lib/email'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface EmployeeReport {
  id: string
  name: string
  department: string
  assessments: any[]
  latest_score: number
  trend: 'improving' | 'stable' | 'declining'
  risk_level: 'low' | 'medium' | 'high'
}

export const EnhancedAnalytics: React.FC = () => {
  const { appUser } = useAuth()
  const [timeRange, setTimeRange] = useState('30d')
  const [selectedMetric, setSelectedMetric] = useState('wellness')
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<any[]>([])
  const [assessments, setAssessments] = useState<any[]>([])
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set())
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null)
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)

  useEffect(() => {
    if (appUser?.company_id) {
      loadData()
    }
  }, [appUser])

  const loadData = async () => {
    if (!appUser?.company_id) {
      toast.error('No company ID found')
      return
    }

    try {
      setLoading(true)
      const { data: employeeData, error: employeeError } = await db.employees.getByCompany(appUser.company_id)
      if (employeeError) throw employeeError
      setEmployees(employeeData || [])

      const { data: assessmentData, error: assessmentError } = await db.assessments.getByCompany(appUser.company_id)
      if (assessmentError) throw assessmentError
      setAssessments(assessmentData || [])
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  // Group employees by department
  const departmentData = employees.reduce((acc, emp) => {
    const dept = emp.department || 'Unassigned'
    if (!acc[dept]) {
      acc[dept] = {
        name: dept,
        employees: [],
        avgScore: 0,
        riskCount: 0,
        totalAssessments: 0
      }
    }
    
    // Get employee assessments
    const empAssessments = assessments.filter(a => a.employee_id === emp.id)
    const latestAssessment = empAssessments[0] // Assuming sorted by date
    const score = latestAssessment?.overall_metrics?.sentiment_score || 0.5
    
    acc[dept].employees.push({
      ...emp,
      assessments: empAssessments,
      latest_score: score,
      trend: score > 0.7 ? 'improving' : score < 0.4 ? 'declining' : 'stable',
      risk_level: score < 0.4 ? 'high' : score < 0.6 ? 'medium' : 'low'
    })
    
    acc[dept].totalAssessments += empAssessments.length
    if (score < 0.5) acc[dept].riskCount++
    
    return acc
  }, {} as Record<string, any>)

  // Calculate average scores for departments
  Object.values(departmentData).forEach((dept: any) => {
    if (dept.employees.length > 0) {
      dept.avgScore = dept.employees.reduce((sum: number, emp: any) => sum + emp.latest_score, 0) / dept.employees.length
    }
  })

  const toggleDepartment = (deptName: string) => {
    const newExpanded = new Set(expandedDepartments)
    if (newExpanded.has(deptName)) {
      newExpanded.delete(deptName)
    } else {
      newExpanded.add(deptName)
    }
    setExpandedDepartments(newExpanded)
  }

  const showEmployeeReport = (employee: any) => {
    setSelectedEmployee(employee.id)
    
    // Generate detailed report
    const report = generateEmployeeReport(employee)
    
    // Open report in new window
    const reportWindow = window.open('', '_blank', 'width=800,height=600')
    if (reportWindow) {
      reportWindow.document.write(report)
      reportWindow.document.close()
    }
  }

  const sendEmployeeReportEmail = async (employee: any) => {
    try {
      setSendingEmail(employee.id)
      toast.loading('Sending report email...', { id: 'sending-report' })
      
      // Get employee assessments
      const empAssessments = assessments.filter(a => a.employee_id === employee.id)
      const latestAssessment = empAssessments[0] // Assuming sorted by date
      
      if (!latestAssessment) {
        toast.error('No assessment data available for this employee')
        return
      }
      
      // Generate report URL (in a real app, this would be a secure link)
      const reportUrl = `${window.location.origin}/reports?employee=${employee.id}&assessment=${latestAssessment.id}`
      
      // Get HR user email
      const hrEmail = appUser?.email
      if (!hrEmail) {
        toast.error('Could not determine HR email address')
        return
      }
      
      // Get assessment details
      const assessmentDate = format(new Date(latestAssessment.completed_at), 'PPP p')
      const assessmentType = getAssessmentTypeLabel(latestAssessment.assessment_type)
      const overallScore = latestAssessment.overall_metrics?.sentiment_score || 0.5
      const recommendations = latestAssessment.recommendations || []
      
      // Generate email HTML
      const emailHtml = generateAssessmentReportEmail(
        appUser?.email.split('@')[0] || 'HR Manager',
        `${employee.first_name} ${employee.last_name}`,
        assessmentDate,
        assessmentType,
        overallScore,
        recommendations,
        reportUrl
      )
      
      // Send email
      const result = await sendEmail({
        to: hrEmail,
        subject: `Assessment Report: ${employee.first_name} ${employee.last_name}`,
        html: emailHtml
      })
      
      toast.dismiss('sending-report')
      
      if (result.success) {
        toast.success('Assessment report sent to your email')
      } else {
        throw new Error(result.error || 'Failed to send email')
      }
    } catch (error) {
      console.error('Error sending assessment report:', error)
      toast.dismiss('sending-report')
      toast.error('Failed to send assessment report')
    } finally {
      setSendingEmail(null)
    }
  }

  const generateEmployeeReport = (employee: any) => {
    const assessments = employee.assessments || []
    const latestAssessment = assessments[0]
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Wellness Report - ${employee.first_name} ${employee.last_name}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px; 
              line-height: 1.6;
              color: #334155;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              padding: 20px;
              background: linear-gradient(135deg, #059669 0%, #10b981 100%);
              color: white;
              border-radius: 12px;
            }
            .metric { 
              background: #f8fafc; 
              padding: 20px; 
              margin: 15px 0; 
              border-radius: 8px; 
              border-left: 4px solid #059669;
            }
            .metric h3 { 
              margin: 0 0 10px 0; 
              color: #1e293b;
            }
            .score {
              font-size: 2em;
              font-weight: bold;
              color: #059669;
              margin: 10px 0;
            }
            .recommendations { 
              background: #ecfdf5; 
              padding: 20px; 
              border-radius: 8px; 
              border: 1px solid #bbf7d0;
            }
            .recommendations h3 {
              color: #065f46;
              margin-top: 0;
            }
            .recommendations ul {
              margin: 0;
              padding-left: 20px;
            }
            .recommendations li {
              margin: 8px 0;
              color: #047857;
            }
            .assessment-history {
              margin-top: 30px;
            }
            .assessment-item {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 15px;
              margin: 10px 0;
            }
            .date {
              color: #64748b;
              font-size: 0.9em;
            }
            .confidential {
              background: #fef3c7;
              border: 1px solid #f59e0b;
              border-radius: 8px;
              padding: 15px;
              margin: 20px 0;
              text-align: center;
              color: #92400e;
            }
            .qa-section {
              margin-top: 20px;
            }
            .qa-item {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 10px;
            }
            .question {
              font-weight: bold;
              color: #1e293b;
              margin-bottom: 5px;
            }
            .answer {
              color: #334155;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Wellness Assessment Report</h1>
            <h2>${employee.first_name} ${employee.last_name}</h2>
            <p>Department: ${employee.department || 'Not specified'}</p>
            <p>Report Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          
          ${latestAssessment ? `
            <div class="metric">
              <h3>Overall Wellness Score</h3>
              <div class="score">${Math.round((latestAssessment.overall_metrics?.sentiment_score || 0.5) * 100)}%</div>
              <p>Based on latest assessment completed on ${new Date(latestAssessment.completed_at).toLocaleDateString()}</p>
            </div>
            
            <div class="metric">
              <h3>Engagement Level</h3>
              <div class="score">${Math.round((latestAssessment.overall_metrics?.engagement_level || 0.5) * 100)}%</div>
              <p>Measures active participation and enthusiasm at work</p>
            </div>
            
            <div class="metric">
              <h3>Stress Level</h3>
              <div class="score">${Math.round((latestAssessment.overall_metrics?.stress_level || 0.5) * 100)}%</div>
              <p>Current stress indicators and coping mechanisms</p>
            </div>
            
            ${latestAssessment.recommendations && latestAssessment.recommendations.length > 0 ? `
              <div class="recommendations">
                <h3>Personalized Recommendations</h3>
                <ul>
                  ${latestAssessment.recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
            
            ${getAssessmentQuestionsAndAnswers(latestAssessment)}
          ` : `
            <div class="metric">
              <h3>No Assessment Data Available</h3>
              <p>This employee has not completed any wellness assessments yet.</p>
            </div>
          `}
          
          <div class="assessment-history">
            <h3>Assessment History</h3>
            ${assessments.length > 0 ? assessments.map((assessment: any) => `
              <div class="assessment-item">
                <div class="date">Completed: ${new Date(assessment.completed_at).toLocaleDateString()}</div>
                <div>Type: ${getAssessmentTypeLabel(assessment.assessment_type)}</div>
                <div>Score: ${Math.round((assessment.overall_metrics?.sentiment_score || 0.5) * 100)}%</div>
              </div>
            `).join('') : '<p>No assessment history available.</p>'}
          </div>
          
          <div class="confidential">
            <strong>Confidential Information</strong><br>
            This report contains sensitive employee wellness data and should be handled according to your company's privacy policies and HIPAA compliance requirements.
          </div>
        </body>
      </html>
    `
  }

  const getAssessmentQuestionsAndAnswers = (assessment: any) => {
    if (!assessment) return ''
    
    let questionsAndAnswers = []
    
    if (assessment.assessment_type === 'text' && assessment.responses) {
      questionsAndAnswers = assessment.responses.map((qa: any) => ({
        question: qa.question,
        answer: qa.response
      }))
    } else if (assessment.assessment_type === 'mcq' && assessment.responses) {
      // For MCQ, we need to map the responses to the questions
      const mcqQuestions = [
        { id: 'stress_level', question: 'How would you rate your current stress level at work?' },
        { id: 'job_satisfaction', question: 'How satisfied are you with your current role and responsibilities?' },
        { id: 'workload_management', question: 'How manageable is your current workload?' },
        { id: 'team_support', question: 'How supported do you feel by your team and manager?' },
        { id: 'work_life_balance', question: 'How would you rate your work-life balance?' }
      ]
      
      questionsAndAnswers = Object.entries(assessment.responses).map(([id, value]) => {
        const question = mcqQuestions.find(q => q.id === id)?.question || id
        return {
          question,
          answer: `Rating: ${value}/5`
        }
      })
    } else if (assessment.assessment_type === 'voice' || assessment.assessment_type === 'conversational_voice') {
      // For voice, we extract the transcript
      if (assessment.conversation_data?.transcript) {
        questionsAndAnswers = [{
          question: 'Voice Transcript',
          answer: assessment.conversation_data.transcript
        }]
      } else if (assessment.conversation_data?.conversation_turns) {
        // For conversational voice, extract the conversation turns
        const turns = assessment.conversation_data.conversation_turns
        questionsAndAnswers = turns.filter((turn: any) => turn.speaker === 'user').map((turn: any, index: number) => ({
          question: `Response ${index + 1}`,
          answer: turn.text
        }))
      }
    }
    
    if (questionsAndAnswers.length === 0) return ''
    
    return `
      <div class="qa-section">
        <h3>Assessment Responses</h3>
        ${questionsAndAnswers.map((qa: any) => `
          <div class="qa-item">
            <div class="question">${qa.question}</div>
            <div class="answer">${qa.answer}</div>
          </div>
        `).join('')}
      </div>
    `
  }

  const handleRefresh = async () => {
    setLoading(true)
    await loadData()
    toast.success('Analytics data refreshed')
  }

  const handleExport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      company: appUser?.company_id,
      departments: Object.values(departmentData),
      summary: {
        total_employees: employees.length,
        total_assessments: assessments.length,
        departments_count: Object.keys(departmentData).length
      }
    }
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `wellness-analytics-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    
    toast.success('Analytics report exported successfully')
  }

  const getAssessmentTypeLabel = (type: string) => {
    switch (type) {
      case 'voice': return 'Voice Assessment'
      case 'text': return 'Text Assessment'
      case 'mcq': return 'Quick Survey'
      case 'conversational_voice': return 'Voice Conversation'
      default: return type.charAt(0).toUpperCase() + type.slice(1)
    }
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Enhanced Analytics
            </h1>
            <p className="text-slate-600">
              Comprehensive insights into team wellness with department and individual reports
            </p>
          </div>
          
          <div className="flex gap-3 mt-4 sm:mt-0">
            <select 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              loading={loading}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleExport}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Employees</p>
                <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
                <p className="text-xs text-slate-500">Across {Object.keys(departmentData).length} departments</p>
              </div>
              <div className="p-2 bg-sage-100 rounded-lg">
                <Users className="w-6 h-6 text-sage-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Assessments</p>
                <p className="text-2xl font-bold text-slate-900">{assessments.length}</p>
                <p className="text-xs text-slate-500">Completed assessments</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Avg Wellness Score</p>
                <p className="text-2xl font-bold text-slate-900">
                  {employees.length > 0 && Object.keys(departmentData).length > 0
                    ? Math.round(Object.values(departmentData).reduce((sum: number, dept: any) => sum + dept.avgScore, 0) / Object.keys(departmentData).length * 100)
                    : '--'
                  }%
                </p>
                <p className="text-xs text-emerald-600">Company average</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Heart className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">At-Risk Employees</p>
                <p className="text-2xl font-bold text-slate-900">
                  {Object.values(departmentData).reduce((sum: number, dept: any) => sum + dept.riskCount, 0)}
                </p>
                <p className="text-xs text-amber-600">Need attention</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Department Analysis */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Department Analysis</h3>
          
          {Object.keys(departmentData).length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Department Data</h3>
              <p className="text-slate-600">Add employees with departments to see department analysis</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.values(departmentData).map((dept: any) => (
                <div key={dept.name} className="border border-slate-200 rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleDepartment(dept.name)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {expandedDepartments.has(dept.name) ? (
                          <ChevronDown className="w-5 h-5  text-slate-500 mr-2" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-500 mr-2" />
                        )}
                        <div>
                          <h4 className="font-medium text-slate-900">{dept.name}</h4>
                          <p className="text-sm text-slate-500">{dept.employees.length} employees</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-8">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-slate-900">
                            {Math.round(dept.avgScore * 100)}%
                          </div>
                          <div className="text-xs text-slate-500">Avg Score</div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-lg font-semibold text-slate-900">
                            {dept.riskCount}
                          </div>
                          <div className="text-xs text-slate-500">At Risk</div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-lg font-semibold text-slate-900">
                            {dept.totalAssessments}
                          </div>
                          <div className="text-xs text-slate-500">Assessments</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded employee list */}
                  {expandedDepartments.has(dept.name) && (
                    <div className="border-t border-slate-200 p-4">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-4 font-medium text-slate-600">Employee</th>
                            <th className="text-left py-2 px-4 font-medium text-slate-600">Latest Score</th>
                            <th className="text-left py-2 px-4 font-medium text-slate-600">Trend</th>
                            <th className="text-left py-2 px-4 font-medium text-slate-600">Risk Level</th>
                            <th className="text-left py-2 px-4 font-medium text-slate-600">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dept.employees.map((employee: any) => (
                            <tr key={employee.id} className="border-b border-slate-100">
                              <td className="py-3 px-4">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3">
                                    <span className="text-slate-600 text-sm font-medium">
                                      {employee.first_name[0]}{employee.last_name[0]}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-medium text-slate-900">{employee.first_name} {employee.last_name}</div>
                                    <div className="text-xs text-slate-500">{employee.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center">
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    employee.latest_score > 0.7 ? 'bg-emerald-500' : 
                                    employee.latest_score > 0.4 ? 'bg-amber-500' : 'bg-red-500'
                                  }`} />
                                  <span className="font-medium text-slate-900">
                                    {Math.round(employee.latest_score * 100)}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center">
                                  {employee.trend === 'improving' && <TrendingUp className="w-4 h-4 text-emerald-500 mr-1" />}
                                  {employee.trend === 'declining' && <TrendingDown className="w-4 h-4 text-red-500 mr-1" />}
                                  {employee.trend === 'stable' && <div className="w-4 h-4 bg-slate-400 rounded-full mr-1" />}
                                  <span className="text-sm text-slate-600 capitalize">{employee.trend}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  employee.risk_level === 'high' ? 'bg-red-100 text-red-700' : 
                                  employee.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 
                                  'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {employee.risk_level.charAt(0).toUpperCase() + employee.risk_level.slice(1)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex space-x-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => showEmployeeReport(employee)}
                                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Report
                                  </Button>
                                  
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => sendEmployeeReportEmail(employee)}
                                    disabled={sendingEmail === employee.id}
                                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                  >
                                    {sendingEmail === employee.id ? (
                                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent mr-2" />
                                    ) : (
                                      <Mail className="w-4 h-4 mr-2" />
                                    )}
                                    Email Report
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Wellness Trends Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Wellness Trends</h3>
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-sm">
                  <div className="w-3 h-3 bg-sage-500 rounded-full mr-2"></div>
                  <span>Wellness</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <span>Stress</span>
                </div>
              </div>
            </div>
            
            {assessments.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={generateTrendData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748b"
                      fontSize={12}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      formatter={(value: number) => [`${Math.round(value * 100)}%`, value === 0 ? 'Wellness' : 'Stress']}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="wellness" 
                      stroke="#059669" 
                      strokeWidth={3}
                      dot={{ fill: '#059669', strokeWidth: 2, r: 4 }}
                      connectNulls={true}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="stress" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                      connectNulls={true}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">No assessment data available</p>
                  <p className="text-sm text-slate-400">Complete assessments to see trends</p>
                </div>
              </div>
            )}
          </Card>

          {/* Department Comparison */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Department Comparison</h3>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Button>
            </div>
            
            {Object.keys(departmentData).length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.values(departmentData)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#64748b" 
                      fontSize={12}
                      tickFormatter={(value) => `${Math.round(value * 100)}%`}
                    />
                    <Tooltip 
                      formatter={(value: number) => [`${Math.round(value * 100)}%`, 'Avg Score']}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="avgScore" 
                      fill="#059669" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-medium">No department data available</p>
                  <p className="text-sm text-slate-400">Add employees with departments to see comparison</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Risk Distribution */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Risk Distribution</h3>
          
          {employees.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="col-span-1">
                <div className="space-y-4">
                  {getRiskDistribution().map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 ${item.color} rounded-full mr-3`}></div>
                        <span className="text-sm font-medium text-slate-900">{item.name}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm text-slate-600 mr-3">{item.value}%</span>
                        <span className="text-sm text-slate-500">({item.count})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="col-span-2">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getRiskDistribution()}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {getRiskDistribution().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.colorHex} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: number) => [`${value}%`, 'Percentage']}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No risk data available</p>
              <p className="text-sm text-slate-400">Complete assessments to see risk distribution</p>
            </div>
          )}
        </Card>

        {/* AI Insights */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center">
              <Brain className="w-5 h-5 mr-2 text-sage-600" />
              AI-Generated Insights
            </h3>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              View All
            </Button>
          </div>
          
          {assessments.length > 0 ? (
            <div className="space-y-4">
              {generateAIInsights().map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border-l-4 ${insight.color} ${insight.bgColor} p-4 rounded-r-lg`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`font-medium ${insight.textColor} mb-1`}>
                        {insight.title}
                      </h4>
                      <p className={`text-sm ${insight.textColor} opacity-90 mb-2`}>
                        {insight.message}
                      </p>
                      <p className="text-xs text-slate-500">
                        {insight.time}
                      </p>
                    </div>
                    <div className="ml-4">
                      <insight.icon className={`w-5 h-5 ${insight.iconColor}`} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">No insights available</p>
              <p className="text-sm text-slate-400">Complete assessments to generate AI insights</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  )

  // Helper functions
  function generateTrendData() {
    if (assessments.length === 0) return []
    
    // Group assessments by date
    const groupedByDate = assessments.reduce((acc, assessment) => {
      const date = assessment.completed_at.split('T')[0]
      if (!acc[date]) {
        acc[date] = {
          date,
          assessments: []
        }
      }
      acc[date].assessments.push(assessment)
      return acc
    }, {} as Record<string, any>)
    
    // Calculate averages for each date
    return Object.values(groupedByDate).map((day: any) => {
      const avgWellness = day.assessments.reduce((sum: number, a: any) => 
        sum + (a.overall_metrics?.sentiment_score || 0.5), 0) / day.assessments.length
      
      const avgStress = day.assessments.reduce((sum: number, a: any) => 
        sum + (a.overall_metrics?.stress_level || 0.5), 0) / day.assessments.length
      
      return {
        date: day.date,
        wellness: avgWellness,
        stress: avgStress
      }
    }).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  function getRiskDistribution() {
    const riskCounts = { low: 0, medium: 0, high: 0 }
    let totalEmployees = 0
    
    Object.values(departmentData).forEach((dept: any) => {
      dept.employees.forEach((emp: any) => {
        riskCounts[emp.risk_level]++
        totalEmployees++
      })
    })
    
    return [
      { 
        name: 'Low Risk', 
        value: totalEmployees > 0 ? Math.round((riskCounts.low / totalEmployees) * 100) : 0,
        count: riskCounts.low,
        color: 'bg-emerald-500',
        colorHex: '#10b981'
      },
      { 
        name: 'Medium Risk', 
        value: totalEmployees > 0 ? Math.round((riskCounts.medium / totalEmployees) * 100) : 0,
        count: riskCounts.medium,
        color: 'bg-amber-500',
        colorHex: '#f59e0b'
      },
      { 
        name: 'High Risk', 
        value: totalEmployees > 0 ? Math.round((riskCounts.high / totalEmployees) * 100) : 0,
        count: riskCounts.high,
        color: 'bg-red-500',
        colorHex: '#ef4444'
      }
    ]
  }

  function generateAIInsights() {
    if (assessments.length === 0) return []
    
    const insights = []
    
    // Department insights
    if (Object.keys(departmentData).length > 0) {
      const highestRiskDept = Object.values(departmentData)
        .sort((a: any, b: any) => b.riskCount - a.riskCount)[0]
      
      if (highestRiskDept && highestRiskDept.riskCount > 0) {
        insights.push({
          type: 'risk',
          title: `${highestRiskDept.name} Department Needs Attention`,
          message: `${highestRiskDept.riskCount} employees showing signs of stress or burnout`,
          time: 'Based on recent assessments',
          color: 'border-red-500',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          iconColor: 'text-red-500',
          icon: AlertTriangle
        })
      }
      
      const highestScoreDept = Object.values(departmentData)
        .sort((a: any, b: any) => b.avgScore - a.avgScore)[0]
      
      if (highestScoreDept && highestScoreDept.avgScore > 0.7) {
        insights.push({
          type: 'positive',
          title: `${highestScoreDept.name} Shows Strong Engagement`,
          message: `Team wellness score of ${Math.round(highestScoreDept.avgScore * 100)}% indicates positive environment`,
          time: 'Based on recent assessments',
          color: 'border-emerald-500',
          bgColor: 'bg-emerald-50',
          textColor: 'text-emerald-700',
          iconColor: 'text-emerald-500',
          icon: TrendingUp
        })
      }
    }
    
    // Overall insights
    const totalAssessments = assessments.length
    if (totalAssessments > 0) {
      insights.push({
        type: 'info',
        title: 'Assessment Participation',
        message: `${totalAssessments} assessments completed by ${employees.filter((e: any) => 
          assessments.some(a => a.employee_id === e.id)).length} employees`,
        time: 'Ongoing tracking',
        color: 'border-blue-500',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        iconColor: 'text-blue-500',
        icon: FileText
      })
    }
    
    // If we don't have enough real insights, add some placeholders
    if (insights.length < 3) {
      insights.push({
        type: 'neutral',
        title: 'Wellness Program Recommendation',
        message: 'Consider implementing regular team check-ins to improve communication',
        time: 'AI recommendation',
        color: 'border-sage-500',
        bgColor: 'bg-sage-50',
        textColor: 'text-sage-700',
        iconColor: 'text-sage-500',
        icon: Brain
      })
    }
    
    return insights
  }
}